import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaCloudUploadAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import { getErrorMessage } from '../utils/errorMessage';

/**
 * AdminSponsoredRowsPage — CRUD for fuldbredde banner-reklamer.
 *
 * Format (Jul 2026, per user): ÉT bannerbillede pr. række, ingen
 * produktkarrusel. Variabel højde (small/medium/large). Alt der vises
 * på opskriftssiden skal komme herfra — ingen seed/demo-annoncer.
 *
 * Endpoints:
 *   GET    /api/admin/sponsored-rows
 *   POST   /api/admin/sponsored-rows
 *   PUT    /api/admin/sponsored-rows/{id}
 *   DELETE /api/admin/sponsored-rows/{id}
 */

const EMPTY_FORM = {
  sponsor_name: '',
  banner_image: '',
  sponsor_logo: '',
  heading: '',
  subheading: '',
  cta_text: 'Læs mere',
  cta_link: '',
  tint: 'blue',
  height_size: 'medium',
  country: 'DK',
  active: true,
  is_promo: false,
};

const TINT_OPTIONS = [
  { value: 'blue', label: 'Blå' },
  { value: 'green', label: 'Grøn' },
  { value: 'amber', label: 'Amber' },
  { value: 'neutral', label: 'Neutral (grå)' },
];

const HEIGHT_OPTIONS = [
  { value: 'small', label: 'Kompakt (mindre luft)' },
  { value: 'medium', label: 'Standard' },
  { value: 'large', label: 'Luftig (mere luft)' },
];

/**
 * ImageUploadField — genbrugelig fil-uploader for sponsor-billeder.
 *
 * Upload → Cloudinary via `/api/upload?folder=sponsored-rows` → auto-udfyld
 * URL-feltet → live preview. Manuel URL kan stadig indsættes som fallback.
 * Bruges både til banner-billede (påkrævet) og sponsor-logo (valgfrit).
 */
const ImageUploadField = ({
  label,
  value,
  onChange,
  required = false,
  testId,
  previewClassName = 'h-24 w-full object-cover',
  helpText,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Kun billeder er tilladt');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Billedet må maks. være 5 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload?folder=sponsored-rows`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url || res.data?.image_url;
      if (!url) throw new Error('Ingen URL modtaget fra upload');
      onChange(url);
      toast.success('Billede uploadet');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(getErrorMessage(err, 'Kunne ikke uploade billede'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const clear = () => onChange('');

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-700">
        {label}{required && ' *'}
      </div>

      {/* Preview / drop zone */}
      {value ? (
        <div className="relative rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
          <img
            src={value}
            alt="preview"
            className={previewClassName}
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
          <button
            type="button"
            onClick={clear}
            className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow text-gray-700 hover:text-red-600"
            title="Fjern billede"
            data-testid={`${testId}-clear`}
          >
            <FaTimes size={12} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors ${uploading ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'}`}
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <>
              <FaSpinner className="animate-spin text-blue-500" size={22} />
              <span className="text-sm text-blue-700 font-medium">Uploader…</span>
            </>
          ) : (
            <>
              <FaCloudUploadAlt className="text-gray-400" size={28} />
              <span className="text-sm text-gray-600">
                <span className="font-semibold text-blue-600">Klik for at uploade</span>{' '}
                eller træk et billede hertil
              </span>
              <span className="text-xs text-gray-400">JPG, PNG, WebP · maks. 5 MB</span>
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-file-input`}
      />

      {/* Manuel URL fallback */}
      <div>
        <div className="text-xs text-gray-500 mb-1">Eller indsæt URL manuelt:</div>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="w-full border rounded-lg px-3 py-2 text-sm"
          data-testid={testId}
        />
      </div>

      {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
    </div>
  );
};

const AdminSponsoredRowsPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => {
    const sessionToken = localStorage.getItem('session_token');
    return sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};
  };

  const fetchRows = async () => {
    try {
      const res = await axios.get(`${API}/admin/sponsored-rows`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching sponsored rows:', err);
      toast.error('Kunne ikke hente sponsor-rækker');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Kun admin har adgang');
      navigate('/');
      return;
    }
    fetchRows();
    // eslint-disable-next-line
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      sponsor_name: row.sponsor_name || '',
      banner_image: row.banner_image || '',
      sponsor_logo: row.sponsor_logo || '',
      heading: row.heading || '',
      subheading: row.subheading || '',
      cta_text: row.cta_text || 'Læs mere',
      cta_link: row.cta_link || '',
      tint: row.tint || 'blue',
      height_size: row.height_size || 'medium',
      country: row.country || 'DK',
      active: row.active !== false,
      is_promo: row.is_promo === true,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.sponsor_name.trim()) return toast.error('Sponsor-navn er påkrævet');
    if (!form.banner_image.trim()) return toast.error('Bannerbillede-URL er påkrævet');
    if (!form.heading.trim()) return toast.error('Overskrift er påkrævet');
    if (!form.cta_link.trim() && !form.is_promo) return toast.error('CTA-link er påkrævet (eller markér som house/promo)');

    const payload = {
      sponsor_name: form.sponsor_name.trim(),
      banner_image: form.banner_image.trim(),
      sponsor_logo: form.sponsor_logo.trim() || null,
      heading: form.heading.trim(),
      subheading: form.subheading.trim() || null,
      cta_text: form.cta_text.trim() || 'Læs mere',
      cta_link: form.cta_link.trim(),
      tint: form.tint,
      height_size: form.height_size,
      country: form.country || 'DK',
      active: !!form.active,
      is_promo: !!form.is_promo,
    };

    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/sponsored-rows/${editing.id}`, payload, {
          withCredentials: true, headers: authHeaders(),
        });
        toast.success('Sponsor-række opdateret');
      } else {
        await axios.post(`${API}/admin/sponsored-rows`, payload, {
          withCredentials: true, headers: authHeaders(),
        });
        toast.success('Sponsor-række oprettet');
      }
      setShowModal(false);
      fetchRows();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(getErrorMessage(err, 'Kunne ikke gemme'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Slet sponsor-række "${row.sponsor_name} — ${row.heading}"?`)) return;
    try {
      await axios.delete(`${API}/admin/sponsored-rows/${row.id}`, {
        withCredentials: true, headers: authHeaders(),
      });
      toast.success('Sponsor-række slettet');
      fetchRows();
    } catch (err) {
      toast.error('Kunne ikke slette');
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.put(`${API}/admin/sponsored-rows/${row.id}`, { active: !row.active }, {
        withCredentials: true, headers: authHeaders(),
      });
      fetchRows();
    } catch (err) {
      toast.error('Kunne ikke opdatere aktiv-status');
    }
  };

  if (loading) return <div className="p-8 text-center">Indlæser…</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-sponsored-rows-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sponsor-rækker</h1>
          <p className="text-sm text-gray-600 mt-1">
            Fuldbredde banner-reklamer inde i opskriftsgriddet (hver 12. opskrift).
          </p>
          <div className="mt-2 inline-flex items-center gap-2 text-xs bg-blue-50 text-blue-800 px-3 py-1.5 rounded-full border border-blue-200">
            <span className="font-semibold">👁️ Vises for Light-brugere</span>
            <span className="text-blue-500">·</span>
            <span>Skjules for Gratis, Pro, Editor og Admin</span>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
          data-testid="new-sponsored-row-btn"
        >
          <FaPlus /> Ny sponsor-række
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 p-10 text-center text-gray-500">
          Ingen sponsor-rækker endnu. Opret den første ovenfor.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div
              key={row.id}
              className="rounded-xl border bg-white p-4 flex items-center gap-4"
              data-testid={`sponsored-row-item-${row.id}`}
            >
              {row.banner_image ? (
                <img src={row.banner_image} alt={row.heading}
                     className="h-14 w-24 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="h-14 w-24 rounded bg-gray-100 flex items-center justify-center text-xs text-gray-400 flex-shrink-0">
                  Intet banner
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 truncate">
                  {row.sponsor_name} <span className="text-gray-400">·</span> {row.heading}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {row.country} · tint={row.tint} · størrelse={row.height_size || 'medium'}
                  {row.is_promo && ' · house/promo'}
                </div>
              </div>
              <div className="text-xs text-gray-500 whitespace-nowrap">
                👁 {row.impressions || 0} · 🖱 {row.clicks || 0}
              </div>
              <button
                onClick={() => toggleActive(row)}
                className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${row.active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}
                title={row.active ? 'Aktiv — klik for at deaktivere' : 'Inaktiv — klik for at aktivere'}
                data-testid={`toggle-active-${row.id}`}
              >
                {row.active ? <><FaEye className="inline mr-1"/>Aktiv</> : <><FaEyeSlash className="inline mr-1"/>Inaktiv</>}
              </button>
              <button
                onClick={() => openEdit(row)}
                className="text-blue-600 hover:text-blue-800 p-2"
                data-testid={`edit-sponsored-row-${row.id}`}
                title="Rediger"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => handleDelete(row)}
                className="text-red-600 hover:text-red-800 p-2"
                data-testid={`delete-sponsored-row-${row.id}`}
                title="Slet"
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <h2 className="text-xl font-bold">
                {editing ? 'Rediger sponsor-række' : 'Ny sponsor-række'}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm col-span-2">Sponsor-navn *
                  <input type="text" required className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.sponsor_name}
                    onChange={e => setForm(f => ({ ...f, sponsor_name: e.target.value }))}
                    data-testid="form-sponsor-name" />
                </label>
                <div className="col-span-2">
                  <ImageUploadField
                    label="Bannerbillede"
                    required
                    value={form.banner_image}
                    onChange={(url) => setForm(f => ({ ...f, banner_image: url }))}
                    testId="form-banner-image"
                    previewClassName="h-32 w-full object-cover"
                    helpText="Fuldbredde-billedet der vises som selve banneret."
                  />
                </div>
                <div className="col-span-2">
                  <ImageUploadField
                    label="Sponsor-logo (valgfrit)"
                    value={form.sponsor_logo}
                    onChange={(url) => setForm(f => ({ ...f, sponsor_logo: url }))}
                    testId="form-sponsor-logo"
                    previewClassName="h-16 w-auto object-contain mx-auto"
                    helpText="Lille logo der vises oven på banneret. Efterlad tomt hvis banneret allerede indeholder logoet."
                  />
                </div>
                <label className="text-sm col-span-2">Overskrift *
                  <input type="text" required className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.heading}
                    onChange={e => setForm(f => ({ ...f, heading: e.target.value }))}
                    data-testid="form-heading" />
                </label>
                <label className="text-sm col-span-2">Undertekst
                  <input type="text" className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.subheading}
                    onChange={e => setForm(f => ({ ...f, subheading: e.target.value }))}
                    data-testid="form-subheading" />
                </label>
                <label className="text-sm">CTA-tekst
                  <input type="text" className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.cta_text}
                    onChange={e => setForm(f => ({ ...f, cta_text: e.target.value }))}
                    data-testid="form-cta-text" />
                </label>
                <label className="text-sm">CTA-link {!form.is_promo && '*'}
                  <input type="url" className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.cta_link}
                    onChange={e => setForm(f => ({ ...f, cta_link: e.target.value }))}
                    placeholder="https://..." data-testid="form-cta-link" />
                </label>
                <label className="text-sm">Farvetone
                  <select className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.tint}
                    onChange={e => setForm(f => ({ ...f, tint: e.target.value }))}
                    data-testid="form-tint">
                    {TINT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className="text-sm">Størrelse (luft omkring)
                  <select className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.height_size}
                    onChange={e => setForm(f => ({ ...f, height_size: e.target.value }))}
                    data-testid="form-height">
                    {HEIGHT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className="text-sm">Land
                  <input type="text" className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.country} maxLength={2}
                    onChange={e => setForm(f => ({ ...f, country: e.target.value.toUpperCase() }))}
                    data-testid="form-country" />
                </label>
                <label className="text-sm flex items-center gap-2 mt-6">
                  <input type="checkbox" checked={form.active}
                    onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                    data-testid="form-active" />
                  Aktiv
                </label>
                <label className="text-sm flex items-center gap-2 col-span-2">
                  <input type="checkbox" checked={form.is_promo}
                    onChange={e => setForm(f => ({ ...f, is_promo: e.target.checked }))}
                    data-testid="form-is-promo" />
                  House/promo (klik → /advertise)
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-600">Annuller</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                  data-testid="form-save">
                  {saving ? 'Gemmer…' : (editing ? 'Gem ændringer' : 'Opret')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSponsoredRowsPage;
